import { useQueryClient } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { useState } from "react";

import {
  ApiReportGoalChartTypeChoices,
  ReportGoalType,
  ReportTargetType,
  useGetReassessmentReportGoalsQuery,
  useUpdateGoalProgressSummaryMutation,
  useUpdateTargetProgressSummaryMutation,
} from "@api/graphql/types-and-hooks";
import { useOffscreenCapture } from "@components/ChartCapture/hooks/useOffscreenCapture";
import { CaptureJob } from "@components/ChartCapture/types";
import { isDataBearingFailure } from "@components/ChartCapture/utils/isDataBearingFailure";

export interface BackfillBlockedItem {
  kind: "goal" | "target";
  id: string;
  name: string;
}

/**
 * Sign-time safety net: captures a chart image for any approved goal/target that
 * is still missing one (reports broken by the pre-fix bulk-approve, or a
 * single-approve capture failure). Enforces the same invariant as approve-time —
 * a data-bearing chart that can't be captured blocks signing.
 */
export const useReassessmentChartBackfill = (reportId: string | undefined) => {
  const queryClient = useQueryClient();
  const goalsQuery = useGetReassessmentReportGoalsQuery(
    { filter: { reportId: reportId ?? "" } },
    { queryKey: ["get-reassessment-goals", reportId], enabled: !!reportId },
  );
  const { mutateAsync: updateGoal } = useUpdateGoalProgressSummaryMutation({});
  const { mutateAsync: updateTarget } = useUpdateTargetProgressSummaryMutation({});
  const { jobs, progress, capture, handleProgress, handleComplete } = useOffscreenCapture();
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [blockedItems, setBlockedItems] = useState<BackfillBlockedItem[]>([]);

  const runBackfill = async (): Promise<{ ok: boolean }> => {
    if (!reportId) return { ok: true };
    setIsBackfilling(true);
    setBlockedItems([]);
    try {
      const { data } = await goalsQuery.refetch();
      const goalList = (unwrapConnection(data?.getReassessmentReportGoals) as ReportGoalType[]) ?? [];

      const missingGoals = goalList.filter((g) => g.summaryReviewed && !g.excludeFromReport && !g.signedChartImageUrl);
      const missingTargets = goalList.flatMap((g) =>
        ((unwrapConnection(g.reporttargetSet) as ReportTargetType[]) ?? [])
          .filter((t) => t.summaryReviewed && !t.excludeFromReport && !t.signedChartImageUrl)
          .map((target) => ({ target, goalId: g.id })),
      );

      const jobsToRun: CaptureJob[] = [
        ...missingGoals.map((goal): CaptureJob => ({ kind: "goal", goal })),
        ...missingTargets.map(({ target, goalId }): CaptureJob => ({ kind: "target", goalId, target })),
      ];
      if (jobsToRun.length === 0) return { ok: true };

      const results = await capture(jobsToRun);

      const blocked: BackfillBlockedItem[] = [];
      const savePromises: Promise<unknown>[] = [];
      for (const result of results) {
        if (result.job.kind === "goal") {
          const goal = result.job.goal;
          if (isDataBearingFailure(result)) {
            blocked.push({ kind: "goal", id: goal.id, name: goal.goalName ?? "Goal" });
            continue;
          }
          if (result.captured && result.imageUri) {
            savePromises.push(
              updateGoal({
                input: {
                  goalId: goal.id,
                  isApproved: true,
                  chartType:
                    (goal.chartType as ApiReportGoalChartTypeChoices) ?? ApiReportGoalChartTypeChoices.Progress,
                  chartImageUri: result.imageUri,
                },
              }),
            );
          }
        } else {
          const target = result.job.target;
          if (isDataBearingFailure(result)) {
            blocked.push({ kind: "target", id: target.id, name: target.targetName ?? "Target" });
            continue;
          }
          if (result.captured && result.imageUri) {
            savePromises.push(
              updateTarget({
                input: { targetId: target.id, isApproved: true, chartImageUri: result.imageUri },
              }),
            );
          }
        }
      }

      await Promise.allSettled(savePromises);
      await queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals"] });

      if (blocked.length > 0) {
        setBlockedItems(blocked);
        return { ok: false };
      }
      return { ok: true };
    } finally {
      setIsBackfilling(false);
    }
  };

  return {
    runBackfill,
    isBackfilling,
    backfillProgress: progress,
    captureJobs: jobs,
    onCaptureProgress: handleProgress,
    onCaptureComplete: handleComplete,
    blockedItems,
    clearBlockedItems: () => setBlockedItems([]),
  };
};
